/**
 * Professional sound synthesis engine for Counter Pump.
 *
 * Renders high-quality procedural audio using a multi-layer architecture:
 * - Oscillator layers (sine, square, sawtooth, triangle) with frequency sweeps
 * - Noise generators (white, pink, brown)
 * - Filtered noise (bandpass, lowpass, highpass shaped noise)
 * - Impulse transients (sharp attack clicks)
 * - ADSR envelopes per layer
 * - Master processing: compression, distortion, highpass/lowpass filtering
 *
 * All rendering is done sample-by-sample into Float32Array buffers,
 * then converted to 16-bit PCM WAV data URIs for Howler.js playback.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type OverSampleType = 'none' | '2x' | '4x';

export interface SoundLayer {
  type: 'oscillator' | 'noise' | 'impulse' | 'filtered-noise';

  // Oscillator params
  waveform?: OscillatorType;
  frequency?: number;
  endFrequency?: number;
  detune?: number;

  // Noise params
  noiseType?: 'white' | 'pink' | 'brown';

  // Filter params (for filtered-noise)
  filterType?: BiquadFilterType;
  filterFreq?: number;
  filterEndFreq?: number;
  filterQ?: number;

  // ADSR envelope
  attack: number;
  decay: number;
  sustain: number;
  release: number;

  // Timing
  startTime?: number;
  duration?: number;

  // Mix
  gain: number;
  pan?: number;
}

export interface SoundDesign {
  name: string;
  duration: number;
  sampleRate: number;
  layers: SoundLayer[];

  // Master processing
  compression?: {
    threshold: number;
    ratio: number;
    attack: number;
    release: number;
  };
  distortion?: {
    amount: number;
    oversample: OverSampleType;
  };
  reverb?: {
    duration: number;
    decay: number;
    wet: number;
  };
  highpass?: number;
  lowpass?: number;
}

// ─── Deterministic PRNG ──────────────────────────────────────────────────────

class PRNG {
  private seed: number;

  constructor(seed: number = 12345) {
    this.seed = seed;
  }

  /** Returns a value in [-1, 1]. */
  next(): number {
    this.seed = (this.seed * 16807 + 0) % 2147483647;
    return (this.seed / 2147483647) * 2 - 1;
  }
}

// ─── Noise Generators ────────────────────────────────────────────────────────

function generateWhiteNoise(rng: PRNG): number {
  return rng.next();
}

/**
 * Pink noise via Paul Kellet's refined method.
 * Approximates -3dB/octave rolloff using 7 first-order filters.
 */
class PinkNoiseGenerator {
  private b0 = 0;
  private b1 = 0;
  private b2 = 0;
  private b3 = 0;
  private b4 = 0;
  private b5 = 0;
  private b6 = 0;

  next(rng: PRNG): number {
    const white = rng.next();
    this.b0 = 0.99886 * this.b0 + white * 0.0555179;
    this.b1 = 0.99332 * this.b1 + white * 0.0750759;
    this.b2 = 0.96900 * this.b2 + white * 0.1538520;
    this.b3 = 0.86650 * this.b3 + white * 0.3104856;
    this.b4 = 0.55000 * this.b4 + white * 0.5329522;
    this.b5 = -0.7616 * this.b5 - white * 0.0168980;
    const pink =
      this.b0 + this.b1 + this.b2 + this.b3 + this.b4 + this.b5 + this.b6 + white * 0.5362;
    this.b6 = white * 0.115926;
    return pink * 0.11; // normalize
  }
}

/**
 * Brown noise (random walk / red noise).
 * Integrates white noise for -6dB/octave rolloff.
 */
class BrownNoiseGenerator {
  private lastOut = 0;

  next(rng: PRNG): number {
    const white = rng.next();
    this.lastOut = (this.lastOut + 0.02 * white) / 1.02;
    return this.lastOut * 3.5; // normalize
  }
}

// ─── Simple Biquad Filter ────────────────────────────────────────────────────

class BiquadFilter {
  private a0 = 1;
  private a1 = 0;
  private a2 = 0;
  private b1 = 0;
  private b2 = 0;
  private z1 = 0;
  private z2 = 0;

  constructor(
    type: BiquadFilterType,
    freq: number,
    q: number,
    sampleRate: number,
  ) {
    this.configure(type, freq, q, sampleRate);
  }

  configure(
    type: BiquadFilterType,
    freq: number,
    q: number,
    sampleRate: number,
  ): void {
    const omega = (2 * Math.PI * freq) / sampleRate;
    const sinO = Math.sin(omega);
    const cosO = Math.cos(omega);
    const alpha = sinO / (2 * q);

    let b0: number, b1: number, b2: number, a0: number, a1: number, a2: number;

    switch (type) {
      case 'lowpass':
        b0 = (1 - cosO) / 2;
        b1 = 1 - cosO;
        b2 = (1 - cosO) / 2;
        a0 = 1 + alpha;
        a1 = -2 * cosO;
        a2 = 1 - alpha;
        break;
      case 'highpass':
        b0 = (1 + cosO) / 2;
        b1 = -(1 + cosO);
        b2 = (1 + cosO) / 2;
        a0 = 1 + alpha;
        a1 = -2 * cosO;
        a2 = 1 - alpha;
        break;
      case 'bandpass':
        b0 = alpha;
        b1 = 0;
        b2 = -alpha;
        a0 = 1 + alpha;
        a1 = -2 * cosO;
        a2 = 1 - alpha;
        break;
      case 'notch':
        b0 = 1;
        b1 = -2 * cosO;
        b2 = 1;
        a0 = 1 + alpha;
        a1 = -2 * cosO;
        a2 = 1 - alpha;
        break;
      default:
        // Fallback to allpass
        b0 = 1;
        b1 = 0;
        b2 = 0;
        a0 = 1;
        a1 = 0;
        a2 = 0;
        break;
    }

    this.a0 = b0 / a0;
    this.a1 = b1 / a0;
    this.a2 = b2 / a0;
    this.b1 = a1 / a0;
    this.b2 = a2 / a0;
  }

  process(input: number): number {
    const output = this.a0 * input + this.z1;
    this.z1 = this.a1 * input - this.b1 * output + this.z2;
    this.z2 = this.a2 * input - this.b2 * output;
    return output;
  }
}

// ─── ADSR Envelope ───────────────────────────────────────────────────────────

function computeADSR(
  t: number,
  totalDuration: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
): number {
  const attackEnd = attack;
  const decayEnd = attack + decay;
  const releaseStart = totalDuration - release;

  if (t < 0) return 0;
  if (t >= totalDuration) return 0;

  if (t < attackEnd) {
    // Attack phase: ramp from 0 to 1
    return attack > 0 ? t / attack : 1;
  }
  if (t < decayEnd) {
    // Decay phase: ramp from 1 to sustain
    const decayProgress = decay > 0 ? (t - attackEnd) / decay : 1;
    return 1 - (1 - sustain) * decayProgress;
  }
  if (t < releaseStart) {
    // Sustain phase
    return sustain;
  }
  // Release phase: ramp from sustain to 0
  const releaseProgress = release > 0 ? (t - releaseStart) / release : 1;
  return sustain * (1 - releaseProgress);
}

// ─── Oscillator ──────────────────────────────────────────────────────────────

function oscillator(
  phase: number,
  waveform: OscillatorType,
): number {
  switch (waveform) {
    case 'sine':
      return Math.sin(phase);
    case 'square':
      return Math.sin(phase) >= 0 ? 1 : -1;
    case 'sawtooth':
      return ((phase % (2 * Math.PI)) / Math.PI) - 1;
    case 'triangle': {
      const normalized = (phase % (2 * Math.PI)) / (2 * Math.PI);
      return 4 * Math.abs(normalized - 0.5) - 1;
    }
    default:
      return Math.sin(phase);
  }
}

// ─── Main SoundDesigner Class ────────────────────────────────────────────────

export class SoundDesigner {
  /**
   * Render a SoundDesign to a Float32Array of PCM samples in [-1, 1].
   * This is a synchronous, CPU-only operation using no browser audio APIs.
   */
  static render(design: SoundDesign): Float32Array {
    const { sampleRate, duration, layers } = design;
    const numSamples = Math.ceil(sampleRate * duration);
    const buffer = new Float32Array(numSamples);

    if (layers.length === 0) return buffer;

    // Render each layer into the buffer
    for (const layer of layers) {
      SoundDesigner.renderLayer(buffer, layer, sampleRate, duration);
    }

    // Apply master processing chain
    SoundDesigner.applyMasterProcessing(buffer, design);

    return buffer;
  }

  /**
   * Render a single layer into the output buffer (additive mixing).
   */
  private static renderLayer(
    buffer: Float32Array,
    layer: SoundLayer,
    sampleRate: number,
    totalDuration: number,
  ): void {
    const layerStart = layer.startTime ?? 0;
    const layerDuration = layer.duration ?? (totalDuration - layerStart);
    const layerEnd = layerStart + layerDuration;

    const startSample = Math.floor(layerStart * sampleRate);
    const endSample = Math.min(
      Math.ceil(layerEnd * sampleRate),
      buffer.length,
    );

    if (layer.gain === 0) return;

    // Per-layer state
    const rng = new PRNG(12345 + Math.floor(layerStart * 1000));
    const pinkGen = new PinkNoiseGenerator();
    const brownGen = new BrownNoiseGenerator();
    let phase = 0;

    // Filter for filtered-noise type
    let filter: BiquadFilter | null = null;
    if (layer.type === 'filtered-noise' && layer.filterType && layer.filterFreq) {
      filter = new BiquadFilter(
        layer.filterType,
        layer.filterFreq,
        layer.filterQ ?? 1,
        sampleRate,
      );
    }

    for (let i = startSample; i < endSample; i++) {
      const t = (i - startSample) / sampleRate; // time within layer
      const envelope = computeADSR(
        t,
        layerDuration,
        layer.attack,
        layer.decay,
        layer.sustain,
        layer.release,
      );

      let sample = 0;

      switch (layer.type) {
        case 'oscillator': {
          const freq = SoundDesigner.interpolateFreq(
            layer.frequency ?? 440,
            layer.endFrequency,
            t,
            layerDuration,
          );
          const detuneFactor = layer.detune
            ? Math.pow(2, layer.detune / 1200)
            : 1;
          phase += ((2 * Math.PI * freq * detuneFactor) / sampleRate);
          sample = oscillator(phase, layer.waveform ?? 'sine');
          break;
        }

        case 'noise': {
          switch (layer.noiseType ?? 'white') {
            case 'white':
              sample = generateWhiteNoise(rng);
              break;
            case 'pink':
              sample = pinkGen.next(rng);
              break;
            case 'brown':
              sample = brownGen.next(rng);
              break;
          }
          break;
        }

        case 'filtered-noise': {
          let raw: number;
          switch (layer.noiseType ?? 'white') {
            case 'white':
              raw = generateWhiteNoise(rng);
              break;
            case 'pink':
              raw = pinkGen.next(rng);
              break;
            case 'brown':
              raw = brownGen.next(rng);
              break;
            default:
              raw = generateWhiteNoise(rng);
          }

          if (filter) {
            // Update filter frequency if sweeping
            if (layer.filterEndFreq !== undefined && layer.filterFreq !== undefined) {
              const progress = layerDuration > 0 ? t / layerDuration : 0;
              const currentFreq =
                layer.filterFreq +
                (layer.filterEndFreq - layer.filterFreq) * progress;
              filter.configure(
                layer.filterType ?? 'lowpass',
                Math.max(20, currentFreq),
                layer.filterQ ?? 1,
                sampleRate,
              );
            }
            sample = filter.process(raw);
          } else {
            sample = raw;
          }
          break;
        }

        case 'impulse': {
          // Sharp transient: exponential decay from 1.0
          const impulseDuration = layerDuration > 0 ? layerDuration : 0.01;
          const impulseDecay = Math.exp(-t / (impulseDuration * 0.15));
          // Add a bit of noise texture to the impulse
          const noiseTexture = 1 + generateWhiteNoise(rng) * 0.3;
          sample = impulseDecay * noiseTexture;
          break;
        }
      }

      buffer[i] += sample * envelope * layer.gain;
    }
  }

  /**
   * Linearly interpolate frequency from start to end over duration.
   */
  private static interpolateFreq(
    startFreq: number,
    endFreq: number | undefined,
    t: number,
    duration: number,
  ): number {
    if (endFreq === undefined || duration === 0) return startFreq;
    const progress = Math.min(1, t / duration);
    return startFreq + (endFreq - startFreq) * progress;
  }

  /**
   * Apply master processing: compression, distortion, filtering.
   */
  private static applyMasterProcessing(
    buffer: Float32Array,
    design: SoundDesign,
  ): void {
    // 1. Highpass filter
    if (design.highpass !== undefined && design.highpass > 0) {
      const hp = new BiquadFilter('highpass', design.highpass, 0.707, design.sampleRate);
      for (let i = 0; i < buffer.length; i++) {
        buffer[i] = hp.process(buffer[i]);
      }
    }

    // 2. Lowpass filter
    if (design.lowpass !== undefined && design.lowpass > 0) {
      const lp = new BiquadFilter('lowpass', design.lowpass, 0.707, design.sampleRate);
      for (let i = 0; i < buffer.length; i++) {
        buffer[i] = lp.process(buffer[i]);
      }
    }

    // 3. Distortion (waveshaping)
    if (design.distortion) {
      const amount = design.distortion.amount;
      for (let i = 0; i < buffer.length; i++) {
        const x = buffer[i] * amount;
        buffer[i] = Math.tanh(x);
      }
    }

    // 4. Compression (simple feed-forward dynamics processor)
    if (design.compression) {
      const { threshold, ratio, attack, release } = design.compression;
      let envelope = 0;
      for (let i = 0; i < buffer.length; i++) {
        const abs = Math.abs(buffer[i]);
        const timeConst = abs > envelope ? attack : release;
        const coeff = 1 - Math.exp(-1 / (design.sampleRate * Math.max(timeConst, 0.0001)));
        envelope += (abs - envelope) * coeff;

        if (envelope > threshold) {
          const excess = envelope - threshold;
          const gain = threshold + excess / ratio;
          const reduction = envelope > 0 ? gain / envelope : 1;
          buffer[i] *= reduction;
        }
      }
    }

    // 5. Reverb (simple feedback comb filter)
    if (design.reverb) {
      const { duration: revDur, decay, wet } = design.reverb;
      const delaySamples = Math.floor(design.sampleRate * revDur * 0.05);
      if (delaySamples > 0 && delaySamples < buffer.length) {
        const delayLine = new Float32Array(delaySamples);
        let writePos = 0;
        for (let i = 0; i < buffer.length; i++) {
          const delayed = delayLine[writePos];
          const input = buffer[i];
          delayLine[writePos] = input + delayed * decay;
          writePos = (writePos + 1) % delaySamples;
          buffer[i] = input * (1 - wet) + delayed * wet;
        }
      }
    }

    // 6. Final hard limiter to ensure [-1, 1]
    for (let i = 0; i < buffer.length; i++) {
      if (buffer[i] > 1) buffer[i] = 1;
      else if (buffer[i] < -1) buffer[i] = -1;
    }
  }

  /**
   * Convert Float32Array samples to 16-bit PCM WAV ArrayBuffer.
   */
  static audioBufferToWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
    const numSamples = samples.length;
    const numChannels = 1;
    const bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
    const blockAlign = numChannels * (bitsPerSample / 8);
    const dataSize = numSamples * blockAlign;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeStr = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);

    for (let i = 0; i < numSamples; i++) {
      const clamped = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(44 + i * 2, clamped * 0x7fff, true);
    }

    return buffer;
  }

  /**
   * Convert Float32Array samples to a base64 WAV data URI.
   */
  static audioBufferToDataUri(samples: Float32Array, sampleRate: number): string {
    const wavBuf = SoundDesigner.audioBufferToWav(samples, sampleRate);
    const bytes = new Uint8Array(wavBuf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return 'data:audio/wav;base64,' + btoa(binary);
  }

  /**
   * Convenience: render a design and return a data URI.
   */
  static renderToDataUri(design: SoundDesign): string {
    const samples = SoundDesigner.render(design);
    return SoundDesigner.audioBufferToDataUri(samples, design.sampleRate);
  }
}

// ─── Water Splash SFX (Phase B3) ────────────────────────────────────────────
//
// Drives the splash SFX off the Protocol v7 `is_in_water` bit.
// Audio-manager registers `splashDesign` as the `splash` Howl; the renderer
// instantiates one WaterEdgeTracker, feeds it per-tick player snapshots, and
// triggers the SFX on each rising edge id returned by `step()`.
//
// Per physics_findings.md §7 the server emits a `water_splash` GameEvent on
// the rising edge already — the tracker is the local fallback for clients
// that lost the event packet (UDP-style snapshot interp), and is also what
// drives the local-player splash without round-tripping through the event
// queue. Both paths converge on the same Howl, so deduplication is implicit.

/** Splash SoundDesign — short impulse + low-pass filtered white noise burst,
 * mimicking water displacement on a footfall. ~120 ms total. */
export const splashDesign: SoundDesign = {
  name: 'splash',
  duration: 0.12,
  sampleRate: 44100,
  layers: [
    // Initial impulse — the surface break.
    { type: 'impulse', attack: 0, decay: 0, sustain: 1, release: 0, gain: 0.45, duration: 0.005 },
    // Low-frequency body — the depth of displacement.
    { type: 'oscillator', waveform: 'sine', frequency: 90, endFrequency: 50,
      attack: 0.001, decay: 0.02, sustain: 0.06, release: 0.04, gain: 0.30 },
    // Mid-frequency swoosh — water rushing back into the cavity.
    { type: 'filtered-noise', noiseType: 'white', filterType: 'bandpass',
      filterFreq: 1200, filterQ: 1.2,
      attack: 0.002, decay: 0.02, sustain: 0.08, release: 0.02, gain: 0.20 },
    // Tail droplets — high-frequency click decay.
    { type: 'filtered-noise', noiseType: 'pink', filterType: 'highpass',
      filterFreq: 3500, filterQ: 0.8,
      attack: 0.005, decay: 0.04, sustain: 0.04, release: 0.03, gain: 0.10 },
  ],
  compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.012 },
};

/** Output of `WaterEdgeTracker.step()` — a list of player IDs whose
 *  `is_in_water` just transitioned false → true. The renderer plays one
 *  splash SFX per id. */
export type WaterRisingEdgeIds = readonly number[];

/**
 * Per-player rising-edge detector for `is_in_water`. Maintains the previous
 * tick's wet flags and reports the IDs that rose this tick. Falling edges
 * are silent — the spec hook is reserved for a future "out of water"
 * ripple, see physics_findings.md §7.2.
 *
 * Memory: O(active players); cleared explicitly via `forget(id)` when a
 * player disconnects so the map doesn't leak between rounds.
 */
export class WaterEdgeTracker {
  private wetFlags = new Map<number, boolean>();

  /** Process this tick's snapshot. Returns the IDs that just entered water. */
  step(players: ReadonlyArray<{ id: number; isInWater?: boolean }>): WaterRisingEdgeIds {
    const rising: number[] = [];
    const seen = new Set<number>();

    for (const p of players) {
      seen.add(p.id);
      const wasWet = this.wetFlags.get(p.id) === true;
      const isWet = p.isInWater === true;
      if (!wasWet && isWet) {
        rising.push(p.id);
      }
      this.wetFlags.set(p.id, isWet);
    }

    // Drop entries for players who weren't in this snapshot — they
    // disconnected or were culled. Prevents the map from growing
    // unbounded across long matches.
    for (const id of this.wetFlags.keys()) {
      if (!seen.has(id)) {
        this.wetFlags.delete(id);
      }
    }

    return rising;
  }

  /** Forget a single player. Called on disconnect / round reset. */
  forget(playerId: number): void {
    this.wetFlags.delete(playerId);
  }

  /** Reset all tracked state. Called on full snapshot replay. */
  reset(): void {
    this.wetFlags.clear();
  }
}
